import { useCallback, useMemo } from 'react'

import { TabSwitch, useTableFilters } from '@circlefin/components'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import {
  BlockchainRecipientAddressOwner,
  RecipientAddressFilterBy,
  useBlockchainRecipientAddressesWithMetadataQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { AddressBookFilter } from '../../filters'

export interface AddressBookTableFilterProps {
  /**
   * Current tab.
   */
  currentTab: BlockchainRecipientAddressOwner
  /**
   * Callback on tab changed.
   */
  onTabChange: (tab: BlockchainRecipientAddressOwner) => void
}

export const AddressBookTableFilters: React.FC<AddressBookTableFilterProps> = ({
  currentTab,
  onTabChange,
}) => {
  const { t } = useTranslation('address-book/containers')

  const { travelRuleSingaporeRequired, travelRuleRequired } =
    useTravelRuleRequired()
  const { clear } = useTableFilters()

  const { data, loading } = useBlockchainRecipientAddressesWithMetadataQuery({
    variables: {
      filter: { show: RecipientAddressFilterBy.ALL_ADDRESSES },
    },
  })

  const handleTabChange = useCallback(
    (newTab: string) => {
      // Clear table filters on tab change
      clear()
      onTabChange(newTab as BlockchainRecipientAddressOwner)
    },
    [onTabChange, clear],
  )

  /**
   * We have different filters based on the travel rule.
   * We need to dynamically set the start index to ensure the filters are always right aligned.
   */
  const filterStart = useMemo(
    () => (travelRuleRequired ? 5 : 7),
    [travelRuleRequired],
  )

  return (
    <>
      {!travelRuleSingaporeRequired && (
        <div className="col-span-12 w-full">
          <TabSwitch onChange={handleTabChange} selected={currentTab}>
            <TabSwitch.Tabs variant="underlined-black">
              <TabSwitch.Tab id={BlockchainRecipientAddressOwner.customer}>
                {t`address-book-table.addresses.yours`}
              </TabSwitch.Tab>

              <TabSwitch.Tab
                id={BlockchainRecipientAddressOwner.non_customer_entity}
              >
                {t`address-book-table.addresses.recipient`}
              </TabSwitch.Tab>
            </TabSwitch.Tabs>
          </TabSwitch>
        </div>
      )}

      <AddressBookFilter.Nickname size={2} start={filterStart} />
      <AddressBookFilter.Blockchain size={2} />
      <AddressBookFilter.Currency
        addresses={data?.blockchainRecipientAddressesWithMetadata}
        loading={loading}
        size={2}
      />
      {travelRuleRequired && (
        <AddressBookFilter.Status
          addresses={data?.blockchainRecipientAddressesWithMetadata}
          size={2}
        />
      )}
    </>
  )
}
